import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'

import App from './App'
import { expireSession } from './auth/session'
import './styles.css'

/**
 * Shared QueryClient. Defaults tuned for this app:
 * - `refetchOnWindowFocus: true`  — pick up fresh data when the user
 *   returns to the tab; cheaper than running pollers forever.
 * - `refetchOnReconnect: true`    — catch up after flaky connections.
 * - `refetchIntervalInBackground: false` — pause polling when the tab is
 *   hidden (browsers already throttle but this avoids redundant work).
 * - Short `staleTime` default of 15s — most data is cheap and frequently
 *   updated; per-query overrides tighten or relax as needed.
 * - Per-401: clear session to force login.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: false,
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error: any) => {
        const status = Number(error?.status || 0)
        if (status === 401 || status === 403 || status === 404) return false
        return failureCount < 2
      },
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error: any) => {
      if (Number(error?.status || 0) === 401) {
        expireSession('query_401')
      }
    },
  }),
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
